import {
  Component,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatPaginatorModule } from '@angular/material/paginator'
import { MatTableModule } from '@angular/material/table'
import { ProductRegulation } from '../../../../types/products'
import { TranslationService } from '../../../../services/translation.service'

@Component({
  selector: 'app-regulation-table',
  imports: [MatTableModule, MatPaginatorModule],
  templateUrl: './regulation-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./regulation-table.component.scss'],
})
export class RegulationTableComponent {
  translationService = inject(TranslationService)

  @Input({ required: true }) productRegulations?: ProductRegulation
  @Input({ required: true }) displayedColumns: string[] = []
  @Input({ required: true }) columnNameMap = new Map<string, string>()

  /**
   * Retrieves the name of a column based on the provided column-name mapping for a property
   * @param {string} prop The name of the column
   * @returns
   */
  getColumnName(prop: string): string {
    const mappedName = this.columnNameMap.get(prop)
    if (mappedName) {
      return mappedName
    }
    return ''
  }

  /**
   * Returns string for a nullable boolean value
   * @param {boolean} bool The boolean value
   * @returns {string} The formatted string
   */
  formatNullableBool(bool: boolean | null): string {
    if (bool != null) {
      if (bool === true) {
        return 'Y'
      } else {
        return 'N'
      }
    }
    return 'N/A'
  }

  /**
   * Formats the value of a column based on the type
   * @param {unknown} value The value of the column
   * @returns {string} The formatted value
   */
  formatColumnValue(value: unknown): string {
    switch (typeof value) {
      case 'boolean':
        return this.formatNullableBool(value)
      case 'string':
        return value
      case 'number':
        return value.toString()
      default:
        return 'N/A'
    }
  }

  /**
   * Gets the value of a column for the aggregation row / product
   * @param {string} column The name of the column
   * @returns {string} The value of the column
   */
  getColumnValueForAggregate(column: string): string {
    if (this.productRegulations) {
      return this.formatColumnValue(
        this.productRegulations![column as keyof ProductRegulation],
      )
    }

    return 'N/A'
  }

  /**
   * Checks if a column should have the 'sticky' property
   * @param {string} column The column definition / property
   * @returns {boolean} Flag indicating if the column should have the 'sticky' property
   */
  isStickyColumn(column: string): boolean {
    return column === 'name' || column === 'casNumber'
  }
}
